import { useEffect, useState } from "react";
import { ArrowUpRight, Asterisk, Menu, X } from "lucide-react";
import { scrollToId } from "./motion";
import { useT } from "@/i18n/LangContext";
import LangSwitch from "./LangSwitch";

const go = (href) => (e) => {
  e.preventDefault();
  scrollToId(href);
};

export default function Nav() {
  const t = useT();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const LINKS = [
    { label: t.nav.services, href: "#services" },
    { label: t.nav.approach, href: "#approach" },
    { label: t.nav.studio, href: "#studio" },
    { label: t.nav.contact, href: "#contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-40 border-b backdrop-blur-xl transition-[background-color,border-color] duration-500 ${
        scrolled || menuOpen ? "bg-white/85 border-ink/10" : "bg-white/60 border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 md:px-10">
        <a
          href="#top"
          data-testid="nav-logo"
          onClick={go("#top")}
          className="flex items-baseline gap-2"
          aria-label={t.brandBack}
        >
          <span className="flex items-center gap-1 font-serif text-xl tracking-tight text-ink">
            Encraft
            <Asterisk className="h-5 w-5 text-bee" aria-hidden="true" />
          </span>
          <span className="text-[0.6rem] font-medium uppercase tracking-[0.3em] text-ink/60">
            Studio
          </span>
        </a>
        <nav className="hidden items-center gap-10 md:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              onClick={go(l.href)}
              className="group relative text-sm font-medium text-ink/85 transition-colors duration-300 hover:text-ink"
            >
              {l.label}
              <span
                className="absolute -bottom-1 left-0 h-px w-0 bg-bee transition-[width] duration-300 group-hover:w-full"
                aria-hidden="true"
              />
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-4">
          <span className="hidden md:inline-flex">
            <LangSwitch />
          </span>
          <a
            href="#contact"
            data-testid="nav-request-call-button"
            onClick={go("#contact")}
            className="group flex min-h-[44px] items-center gap-2 whitespace-nowrap rounded-full bg-ink px-4 py-2.5 text-xs font-medium text-white transition-colors duration-300 hover:bg-bee hover:text-ink sm:px-5 sm:text-sm"
          >
            <span className="sm:hidden">{t.nav.requestCallShort}</span>
            <span className="hidden sm:inline">{t.nav.requestCall}</span>
            <ArrowUpRight
              className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              aria-hidden="true"
            />
          </a>
          <button
            type="button"
            data-testid="nav-menu-button"
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            aria-label={menuOpen ? t.nav.menuClose : t.nav.menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-ink/20 text-ink transition-colors duration-300 hover:border-bee md:hidden"
          >
            {menuOpen ? (
              <X className="h-5 w-5" aria-hidden="true" />
            ) : (
              <Menu className="h-5 w-5" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>
      {menuOpen && (
        <nav
          id="mobile-menu"
          data-testid="mobile-menu"
          aria-label="Mobile"
          className="border-t border-ink/10 bg-white/95 px-6 py-4 backdrop-blur-xl md:hidden"
        >
          <ul className="flex flex-col divide-y divide-lavline">
            {LINKS.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  data-testid={`mobile-link-${l.href.slice(1)}`}
                  onClick={(e) => {
                    go(l.href)(e);
                    setMenuOpen(false);
                  }}
                  className="flex min-h-[48px] items-center text-base font-medium text-ink transition-colors duration-300 hover:text-ink/70"
                >
                  {l.label}
                </a>
              </li>
            ))}
            <li className="flex min-h-[48px] items-center">
              <LangSwitch onSwitch={() => setMenuOpen(false)} />
            </li>
          </ul>
        </nav>
      )}
    </header>
  );
}
